// frontend/src/components/ReviewFormComponent.jsx
import { useMemo, useCallback } from "react";

// Helper functions (extracted from RiskReview.jsx)
function groupByHeader(questions, section) {
  const map = new Map();
  for (const q of (questions || []).filter((x) => x.section === section)) {
    const h = q.header || "General";
    if (!map.has(h)) map.set(h, []);
    map.get(h).push(q);
  }

  const natCmp = (a, b) => {
    const ma = /^([A-Za-z]+)(\d+)$/.exec(a.id) || [];
    const mb = /^([A-Za-z]+)(\d+)$/.exec(b.id) || [];
    const ap = ma[1] || "",
      bp = mb[1] || "";
    const an = ma[2] ? parseInt(ma[2], 10) : Number.MAX_SAFE_INTEGER;
    const bn = mb[2] ? parseInt(mb[2], 10) : Number.MAX_SAFE_INTEGER;
    if (ap !== bp) return ap.localeCompare(bp);
    if (an !== bn) return an - bn;
    return a.id.localeCompare(b.id);
  };

  return Array.from(map.entries()).map(([header, items]) => ({
    header,
    items: items.sort(natCmp),
  }));
}

function sectionCount(questions, section) {
  return (questions || []).filter((q) => q.section === section).length;
}

function sectionAnsweredCount(answers, questions, section) {
  const ids = new Set((questions || []).filter((q) => q.section === section).map((q) => q.id));
  let n = 0;
  for (const [k, v] of Object.entries(answers || {})) {
    if (ids.has(k) && (v === "Y" || v === "N" || v === "NA")) n++;
  }
  return n;
}

export default function ReviewFormComponent({
  // Props from useReviewForm hook
  isEditMode,
  step,
  setStep,
  answers,
  setAnswers,
  comments,
  setComments,
  companyQ,
  setCompanyQ,
  companySel,
  setCompanySel,
  suggest,
  reviewDate,
  setReviewDate,
  nextReviewDate,
  setNextReviewDate,
  nextDateTouched,
  setNextDateTouched,
  reviewer,
  setReviewer,
  validationErrors,
  setValidationErrors,
  openCommentId,
  setOpenCommentId,
  schemaStatus,
  suggestStatus,
  saveStatus,
  schema,
  QUESTIONS,
  SECTIONS,
  currentSection,
  score,
  onSave,
  loadSchema,
  handleCompanySelect,
  closeAllComments,

  // Additional props for customization
  title = "Client Risk Review",
  onSaveSuccess,
  onCancel,
  showCancelButton = false,
}) {
  // Computed values
  const grouped = useMemo(() => groupByHeader(QUESTIONS, currentSection), [QUESTIONS, currentSection]);
  const totalInSection = sectionCount(QUESTIONS, currentSection);
  const answeredInSection = sectionAnsweredCount(answers, QUESTIONS, currentSection);

  const canPrev = step > 0;
  const canNext = answeredInSection >= totalInSection && step < SECTIONS.length - 1;
  const isLastStep = step === SECTIONS.length - 1;

  const allAnswered = useMemo(() => SECTIONS.every((sec) => sectionAnsweredCount(answers, QUESTIONS, sec) === sectionCount(QUESTIONS, sec)), [answers, QUESTIONS]);

  const overallProgress = useMemo(() => {
    const totalQuestions = QUESTIONS.length;
    const totalAnswered = Object.keys(answers).filter((k) => ["Y", "N", "NA"].includes(answers[k])).length;
    return totalQuestions > 0 ? (totalAnswered / totalQuestions) * 100 : 0;
  }, [answers, QUESTIONS]);

  // Navigation
  const goPrev = useCallback(() => {
    if (canPrev) {
      closeAllComments();
      setStep((s) => s - 1);
      setValidationErrors([]);
    }
  }, [canPrev, closeAllComments, setStep, setValidationErrors]);

  const goNext = useCallback(() => {
    if (canNext) {
      closeAllComments();
      setStep((s) => s + 1);
      setValidationErrors([]);
    }
  }, [canNext, closeAllComments, setStep, setValidationErrors]);

  // Question comment section
  const questionCommentSection = useCallback(
    (q) => {
      const commentOpen = openCommentId === q.id;
      const hasComment = comments[q.id] && comments[q.id].trim();
      return (
        <div className="question-actions" style={{ marginTop: 8 }}>
          <button
            type="button"
            className={`secondary small ${hasComment ? "has-content" : ""}`}
            onClick={() => setOpenCommentId((p) => (p === q.id ? null : q.id))}
            aria-expanded={commentOpen}>
            {commentOpen ? "Hide comment" : hasComment ? "Edit comment" : "Add comment"}
            {hasComment && !commentOpen && (
              <span className="comment-indicator" aria-hidden="true">
                ●
              </span>
            )}
          </button>
          {commentOpen && (
            <div className="comment-box" style={{ marginTop: 8 }}>
              <label className="sr-only" htmlFor={`comment-${q.id}`}>
                Comment for {q.id}
              </label>
              <textarea
                id={`comment-${q.id}`}
                rows={3}
                value={comments[q.id] || ""}
                onChange={(e) => setComments((prev) => ({ ...prev, [q.id]: e.target.value }))}
                placeholder={`Add notes or observations for question ${q.id}...`}
                autoFocus
              />
              <div className="comment-actions" style={{ marginTop: 4, display: "flex", gap: 8 }}>
                <button type="button" className="secondary tiny" onClick={() => setOpenCommentId(null)}>
                  Done
                </button>
                {hasComment && (
                  <button
                    type="button"
                    className="secondary tiny text-danger"
                    onClick={() => {
                      setComments((prev) => {
                        const n = { ...prev };
                        delete n[q.id];
                        return n;
                      });
                      setOpenCommentId(null);
                    }}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      );
    },
    [comments, openCommentId, setComments, setOpenCommentId]
  );

  // Handle save with success callback
  const handleSave = useCallback(
    (e) => {
      onSave(e, onSaveSuccess);
    },
    [onSave, onSaveSuccess]
  );

  // Loading state
  if (schemaStatus.loading && !schema.QUESTIONS.length) {
    return (
      <div className="container" role="main" aria-label="Loading risk review">
        <h1 className="page-title">{title}</h1>
        <div className="card form-section" aria-live="polite" aria-busy="true">
          <div className="skeleton h16 w200" />
          <div className="skeleton h12 w320 mt8" />
          <div className="skeleton h12 w260 mt8" />
        </div>
      </div>
    );
  }

  // Error state
  if (schemaStatus.error && !schema.QUESTIONS.length) {
    return (
      <div className="container" role="main">
        <h1 className="page-title">{title}</h1>
        <div className="card error-card" role="alert">
          <div className="error-title">Unable to load risk questionnaire</div>
          <div className="error-body">{schemaStatus.error.userMessage}</div>
          {schemaStatus.error.canRetry && (
            <div className="wizard-footer">
              <button className="secondary" onClick={() => loadSchema(true)} disabled={schemaStatus.loading}>
                {schemaStatus.loading ? "Retrying..." : "Try Again"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container" role="main">
      <h1 className="page-title">{title}</h1>

      <div className="progress-indicator" role="progressbar" aria-valuenow={overallProgress} aria-valuemin="0" aria-valuemax="100">
        <div className="progress-bar" style={{ width: `${overallProgress}%` }} />
        <span className="progress-text">Overall: {Math.round(overallProgress)}% complete</span>
      </div>

      {/* Company Card */}
      <div className="card form-section">
        <div className="form-grid-2">
          <div className="f-field">
            <label className="f-label" htmlFor="company-search">
              Company *
            </label>
            {isEditMode ? (
              <input
                id="company-search"
                name="company"
                value={companySel ? companySel.name : `Company ${companySel?.id || ""}`}
                disabled
                readOnly
                style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
              />
            ) : (
              <>
                <input
                  id="company-search"
                  name="company"
                  placeholder="Search company name or registration number"
                  value={companySel ? companySel.name : companyQ}
                  onChange={(e) => {
                    setCompanySel(null);
                    setCompanyQ(e.target.value);
                  }}
                  aria-describedby="company-description company-error"
                  aria-invalid={validationErrors.some((e) => e.field === "company")}
                />
                {suggest.length > 0 && !companySel && (
                  <div className="menu" style={{ marginTop: 8, maxWidth: 520 }}>
                    {suggest.map((s) => (
                      <div key={s.id} className="menu-item" onClick={() => handleCompanySelect(s)} role="option">
                        {s.name} <span className="kv">· {s.registration_no || "Unknown reg"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div id="company-description" className="field-description">
              {isEditMode ? "Company cannot be changed when editing" : "Start typing to search for registered companies"}
            </div>
            {validationErrors.some((e) => e.field === "company") && (
              <div id="company-error" className="error-message" role="alert">
                {validationErrors.find((e) => e.field === "company")?.message}
              </div>
            )}
          </div>

          <div className="f-field">
            <label className="f-label" htmlFor="review-date">
              Review date *
            </label>
            <input
              id="review-date"
              name="reviewDate"
              type="date"
              value={reviewDate}
              onChange={(e) => {
                setReviewDate(e.target.value);
                setValidationErrors((prev) => prev.filter((er) => er.field !== "reviewDate"));
              }}
              aria-invalid={validationErrors.some((e) => e.field === "reviewDate")}
            />
          </div>

          <div className="f-field">
            <label className="f-label" htmlFor="next-review-date">
              Next review date
            </label>
            <input
              id="next-review-date"
              type="date"
              value={nextReviewDate}
              onChange={(e) => {
                setNextDateTouched(true);
                setNextReviewDate(e.target.value);
              }}
            />
          </div>

          <div className="f-field">
            <label className="f-label" htmlFor="reviewer">
              Reviewer
            </label>
            <input
              id="reviewer"
              name="reviewer"
              value={reviewer}
              onChange={(e) => {
                setReviewer(e.target.value);
                setValidationErrors((prev) => prev.filter((er) => er.field !== "reviewer"));
              }}
              placeholder="Enter reviewer name"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" role="tablist">
        {SECTIONS.map((sec, idx) => {
          const sectionAnswered = sectionAnsweredCount(answers, QUESTIONS, sec);
          const sectionTotal = sectionCount(QUESTIONS, sec);
          const isComplete = sectionAnswered === sectionTotal && sectionTotal > 0;
          return (
            <button
              key={sec}
              role="tab"
              className={`tab-btn ${step === idx ? "active" : ""} ${isComplete ? "complete" : ""}`}
              onClick={() => {
                closeAllComments();
                const curTotal = sectionCount(QUESTIONS, SECTIONS[step]);
                const curAnswered = sectionAnsweredCount(answers, QUESTIONS, SECTIONS[step]);
                if (idx <= step || curAnswered === curTotal) setStep(idx);
              }}
              aria-selected={step === idx}>
              Section {sec}
              {isComplete && <span className="tab-check">✓</span>}
            </button>
          );
        })}
      </div>

      {/* Wizard */}
      <div className="card form-section">
        <div className="wizard-head">
          <div className="wizard-title">Section {currentSection}</div>
          <div className="wizard-progress">
            <div className="wizard-progress-bar" style={{ width: `${(answeredInSection / Math.max(totalInSection, 1)) * 100}%` }} />
            <div className="wizard-progress-text">
              {answeredInSection} / {totalInSection} answered
              {totalInSection > 0 && answeredInSection === totalInSection && <span className="section-complete"> - Section complete!</span>}
            </div>
          </div>
        </div>

        {/* Questions */}
        {grouped.map(({ header, items }) => (
          <div key={header} className="question-group">
            <div className="question-header">{header}</div>
            <div className="question-list">
              {items.map((q) => {
                const val = answers[q.id] || "";
                const hasComment = !!(comments[q.id] && comments[q.id].trim());
                return (
                  <div key={q.id} className="question-row">
                    <div className="question-text">
                      <span className="qid">{q.id}</span>
                      <span>{q.label}</span>
                      {hasComment && (
                        <span className="comment-dot" title="This question has a comment">
                          ●
                        </span>
                      )}
                    </div>

                    {/* Radio buttons */}
                    <fieldset className="radio-group">
                      <legend className="sr-only">Answer for {q.id}</legend>
                      {["Y", "N", "NA"].map((opt) => (
                        <label key={opt} className={`radio-chip ${val === opt ? "selected" : ""}`}>
                          <input
                            type="radio"
                            name={q.id}
                            value={opt}
                            checked={val === opt}
                            onChange={(e) => {
                              setAnswers((p) => ({ ...p, [q.id]: e.target.value }));
                              setValidationErrors((p) => p.filter((er) => er.field !== q.id));
                            }}
                          />
                          <span>{opt === "Y" ? "Yes" : opt === "N" ? "No" : "N/A"}</span>
                        </label>
                      ))}
                    </fieldset>

                    {questionCommentSection(q)}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Footer */}
        {!isLastStep ? (
          <div className="wizard-footer">
            <button className="secondary" onClick={goPrev} disabled={!canPrev}>
              ← Back
            </button>
            <button onClick={goNext} disabled={!canNext}>
              Next →
            </button>
          </div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="wizard-footer">
              <button type="button" className="secondary" onClick={goPrev} disabled={!canPrev}>
                ← Back
              </button>
              <div className="toolbar">
                {showCancelButton && onCancel && (
                  <button type="button" className="secondary" onClick={onCancel} style={{ marginRight: 8 }}>
                    Cancel
                  </button>
                )}
                <button type="submit" disabled={!companySel || !allAnswered || saveStatus.loading} className={saveStatus.success ? "success" : saveStatus.error ? "error" : ""}>
                  {saveStatus.loading ? "Saving..." : saveStatus.success ? "Saved!" : isEditMode ? "Update Review" : "Save Review"}
                </button>
              </div>
            </div>
          </form>
        )}

        {(saveStatus.error || saveStatus.success) && (
          <div className={`status-message ${saveStatus.error ? "error" : "success"}`} role="alert">
            {saveStatus.error && (
              <>
                <strong>Save failed:</strong> {saveStatus.error.userMessage}
                {saveStatus.error.canRetry && (
                  <button className="retry-btn" onClick={handleSave}>
                    Try Again
                  </button>
                )}
              </>
            )}
            {saveStatus.success && (
              <>
                <strong>Success!</strong> Risk review has been {isEditMode ? "updated" : "saved"} successfully.
              </>
            )}
          </div>
        )}
      </div>

      {/* Risk Summary */}
      <div className="card form-section">
        <h3>Overall Risk Analysis</h3>
        <div className="detail-grid">
          <div className="summary-item">
            <strong>Total Score</strong>
            <span className={`score-value score-${score.level}`}>{score.total}</span>
          </div>
          <div className="summary-item">
            <strong>Risk Level</strong>
            <span className={`risk-level risk-${score.level}`}>{score.level.replace("_", " ").toUpperCase()}</span>
          </div>
          <div className="summary-item">
            <strong>Section A Average</strong>
            <span>{Math.round((score.avgBySection?.A || 0) * 100)}%</span>
          </div>
          <div className="summary-item">
            <strong>Section B Average</strong>
            <span>{Math.round((score.avgBySection?.B || 0) * 100)}%</span>
          </div>
          <div className="summary-item">
            <strong>Overall Average</strong>
            <span>{Math.round((score.avgOverall || 0) * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
